import React from 'react';
export function useColumnSizing(apiRef, gridConfig) {
    const customized = React.useRef(false);
    React.useEffect(() => {
        const gridApi = apiRef.current;
        if (!gridApi) {
            return undefined;
        }
        let disposed = false;
        let pendingSizing;
        let resizing = false;
        let rootWidth = gridApi.getRootDimensions().root.width;
        let visibilityFrame;
        let initializationViewportDisposer;
        const initialSizing = gridApi
            .getAllColumns()
            .map(({ field, flex, type, width }) => ({
            field,
            flex: flex ?? 0,
            type,
            width
        }));
        const freezeColumns = (columnWidths, excludedField) => {
            const visibleFields = new Set(gridApi.getVisibleColumns().map(({ field }) => field));
            const updatedColumns = gridApi
                .getAllColumns()
                .flatMap(({ computedWidth, field, flex, type }) => {
                if (field === excludedField) {
                    return [];
                }
                const configuredWidth = columnWidths?.[field];
                if (configuredWidth !== undefined) {
                    return [
                        { field, flex: 0, type, width: configuredWidth }
                    ];
                }
                if (visibleFields.has(field) &&
                    flex !== undefined &&
                    flex > 0 &&
                    computedWidth > 0) {
                    return [{ field, flex: 0, type, width: computedWidth }];
                }
                return [];
            });
            if (updatedColumns.length > 0) {
                gridApi.updateColumns(updatedColumns);
            }
        };
        const completeConfigInitialization = (columnWidths) => {
            if (disposed) {
                return;
            }
            freezeColumns(columnWidths);
            customized.current = true;
            gridConfig?.completeColumnInitialization();
        };
        const initialize = async () => {
            await gridConfig?.whenModelInitialized();
            if (disposed) {
                return;
            }
            const { columnWidths } = gridConfig?.wr.$ ?? {};
            const hasConfiguredWidths = gridApi
                .getAllColumns()
                .some(({ field }) => columnWidths?.[field] !== undefined);
            if (!hasConfiguredWidths) {
                gridConfig?.completeColumnInitialization();
                return;
            }
            if (gridApi.getRootDimensions().viewportOuterSize.width > 0) {
                completeConfigInitialization(columnWidths);
                return;
            }
            initializationViewportDisposer = gridApi.subscribeEvent('viewportInnerSizeChange', () => {
                if (gridApi.getRootDimensions().viewportOuterSize.width ===
                    0) {
                    return;
                }
                initializationViewportDisposer?.();
                initializationViewportDisposer = undefined;
                completeConfigInitialization(columnWidths);
            });
        };
        const resizeStartDisposer = gridApi.subscribeEvent('columnResizeStart', (params) => {
            resizing = true;
            if (customized.current) {
                return;
            }
            pendingSizing = getFlexSizing(gridApi, getResizeField(params));
        });
        const resizeStopDisposer = gridApi.subscribeEvent('columnResizeStop', () => {
            resizing = false;
            pendingSizing = undefined;
        });
        const widthChangeDisposer = gridApi.subscribeEvent('columnWidthChange', () => {
            if (!pendingSizing) {
                return;
            }
            if (pendingSizing.length > 0) {
                gridApi.updateColumns(pendingSizing);
            }
            pendingSizing = undefined;
            customized.current = true;
        });
        const autosizeDisposer = gridApi.subscribeEvent('columnSeparatorDoubleClick', (params) => {
            if (!customized.current) {
                pendingSizing = getFlexSizing(gridApi, getResizeField(params));
            }
        });
        const columnsDisposer = gridApi.subscribeEvent('columnsChange', () => {
            if (gridConfig?.isResetting) {
                customized.current = false;
                if (visibilityFrame !== undefined) {
                    cancelAnimationFrame(visibilityFrame);
                    visibilityFrame = undefined;
                }
            }
        });
        const visibilityDisposer = gridApi.subscribeEvent('columnVisibilityModelChange', () => {
            if (!customized.current || gridConfig?.isResetting) {
                return;
            }
            if (visibilityFrame !== undefined) {
                cancelAnimationFrame(visibilityFrame);
            }
            visibilityFrame = requestAnimationFrame(() => {
                visibilityFrame = undefined;
                freezeColumns();
            });
        });
        const viewportDisposer = gridApi.subscribeEvent('resize', () => {
            const nextRootWidth = gridApi.getRootDimensions().root.width;
            if (nextRootWidth === rootWidth) {
                return;
            }
            rootWidth = nextRootWidth;
            if (resizing ||
                (gridConfig && !gridConfig.wr.error) ||
                !customized.current) {
                return;
            }
            gridApi.updateColumns(initialSizing);
            customized.current = false;
        });
        void initialize();
        return () => {
            disposed = true;
            resizeStartDisposer();
            resizeStopDisposer();
            widthChangeDisposer();
            autosizeDisposer();
            columnsDisposer();
            visibilityDisposer();
            viewportDisposer();
            initializationViewportDisposer?.();
            if (visibilityFrame !== undefined) {
                cancelAnimationFrame(visibilityFrame);
            }
        };
    }, [apiRef, gridConfig]);
}
function getFlexSizing(gridApi, excludedField) {
    return gridApi
        .getVisibleColumns()
        .filter(({ field, flex }) => field !== excludedField && flex !== undefined && flex > 0)
        .map(({ computedWidth, field, type }) => ({
        field,
        flex: 0,
        type,
        width: computedWidth
    }));
}
function getResizeField(params) {
    if (typeof params === 'object' &&
        params !== null &&
        'field' in params &&
        typeof params.field === 'string') {
        return params.field;
    }
    return undefined;
}
